/**
 * Правка говорящих в расшифровке — операции над сегментами, без страницы.
 *
 * Зачем отдельным модулем. До 07.10.2026 с метками говорящих можно было сделать
 * одно — переименовать голос ВЕЗДЕ. Этим же действием чинили всё остальное, и
 * выходило плохо:
 *   · поправить ОДНУ реплику, отданную не тому человеку, было нечем;
 *   · вписав в метку имя другого говорящего, человек молча сливал двоих во всём
 *     тексте — попытка исправить одну реплику стирала различие голосов, и
 *     вернуть его было нельзя.
 * Ошибается же разметка часто: за 30 дней до правки (RU, 105 прогонов) она
 * находила восемь и больше «говорящих» в 23 прогонах, а на тестовом заседании
 * из пяти человек 5 сегментов из 100 накрывали реплики двоих.
 *
 * Здесь три разных действия названы разными функциями: переименовать (rename),
 * отдать реплики другому (assign) и объединить двоих (merge). Страница решает,
 * какое из них человек имел в виду, и спрашивает, когда это неочевидно.
 *
 * Сегмент: { text, timestamp, end?, speaker?, isParagraph? } — как в движке
 * расшифровки. Функции меняют переданный массив на месте и возвращают, сколько
 * сегментов изменили.
 */
(function (root) {
    'use strict';

    function isSpeech(s) { return !!(s && !s.isParagraph && s.text); }

    function clean(name) {
        return String(name == null ? '' : name).replace(/\s+/g, ' ').trim().slice(0, 40);
    }

    /** Имена по порядку первого появления. */
    function names(segs) {
        var seen = {}, out = [];
        for (var i = 0; i < (segs || []).length; i++) {
            var s = segs[i];
            if (!isSpeech(s) || !s.speaker) continue;
            if (!seen[s.speaker]) { seen[s.speaker] = 1; out.push(s.speaker); }
        }
        return out;
    }

    function has(segs, name) {
        for (var i = 0; i < (segs || []).length; i++) {
            if (isSpeech(segs[i]) && segs[i].speaker === name) return true;
        }
        return false;
    }

    /**
     * Имя уже носит ДРУГОЙ голос? Тогда «переименовать в него» — это слияние
     * двух людей, и решать его должен человек, а не совпадение букв.
     * Сравнение без учёта регистра и лишних пробелов: «анна» и «Анна» — одно.
     */
    function clash(segs, from, to) {
        to = clean(to);
        if (!to) return null;
        var low = to.toLowerCase(), all = names(segs);
        for (var i = 0; i < all.length; i++) {
            if (all[i] !== from && all[i].toLowerCase() === low) return all[i];
        }
        return null;
    }

    /** Переименовать голос везде. Слияние не делает: при совпадении имён — 0. */
    function rename(segs, from, to) {
        to = clean(to);
        if (!to || to === from || clash(segs, from, to)) return 0;
        var n = 0;
        for (var i = 0; i < segs.length; i++) {
            if (isSpeech(segs[i]) && segs[i].speaker === from) { segs[i].speaker = to; n++; }
        }
        return n;
    }

    /** Объединить двоих: все реплики `from` становятся репликами `into`. */
    function merge(segs, from, into) {
        if (!from || !into || from === into || !has(segs, into)) return 0;
        var n = 0;
        for (var i = 0; i < segs.length; i++) {
            if (isSpeech(segs[i]) && segs[i].speaker === from) { segs[i].speaker = into; n++; }
        }
        return n;
    }

    /** Отдать сегменты с i0 по i1 включительно одному говорящему. */
    function assign(segs, i0, i1, name) {
        name = clean(name);
        if (!name) return 0;
        var a = Math.max(0, Math.min(i0, i1)), b = Math.min(segs.length - 1, Math.max(i0, i1)), n = 0;
        for (var i = a; i <= b; i++) {
            if (!isSpeech(segs[i])) continue;
            if (segs[i].speaker !== name) { segs[i].speaker = name; n++; }
        }
        return n;
    }

    /**
     * Границы абзаца в массиве сегментов. Абзацы строит страница, и отдаёт она
     * сами объекты сегментов, поэтому ищем по тождеству, а не по времени: у двух
     * реплик время может совпасть, у двух объектов — нет.
     */
    function span(segs, paraSegs) {
        if (!paraSegs || !paraSegs.length) return null;
        var first = segs.indexOf(paraSegs[0]), last = segs.indexOf(paraSegs[paraSegs.length - 1]);
        if (first < 0 || last < 0) return null;
        return [Math.min(first, last), Math.max(first, last)];
    }

    /**
     * Имя для нового говорящего: «Говорящий N» с наименьшим свободным N.
     * `tpl` — шаблон страницы с {n}.
     */
    function freeName(segs, tpl) {
        tpl = String(tpl || 'Speaker {n}');
        var taken = {}, all = names(segs);
        for (var i = 0; i < all.length; i++) taken[all[i].toLowerCase()] = 1;
        for (var n = 1; n < 1000; n++) {
            var cand = tpl.replace('{n}', String(n));
            if (!taken[cand.toLowerCase()]) return cand;
        }
        return tpl.replace('{n}', String(all.length + 1));
    }

    /**
     * Разрезать сегмент на два по месту в тексте — когда в одном сегменте слова
     * двоих («Нет, отводов нет. Ответчик?»: ответ истца и следующий вопрос суда
     * распознались одним куском). Место сдвигается к границе слова: резать
     * посреди слова незачем. Время делится по доле текста — точнее взять негде.
     * @returns {boolean} разрезан ли сегмент (false — резать нечего: край)
     */
    function splitAt(segs, index, offset) {
        var s = segs[index];
        if (!isSpeech(s)) return false;
        var text = String(s.text), len = text.length;
        var cut = Math.max(0, Math.min(len, offset | 0));
        /* к ближайшему пробелу слева; если его нет — резать нечего */
        while (cut > 0 && cut < len && !/\s/.test(text.charAt(cut - 1)) && !/\s/.test(text.charAt(cut))) cut--;
        var left = text.slice(0, cut).replace(/\s+$/, ''), right = text.slice(cut).replace(/^\s+/, '');
        if (!left || !right) return false;
        var start = s.timestamp || 0;
        var hasEnd = typeof s.end === 'number' && s.end > start;
        var mid = hasEnd ? start + (s.end - start) * (left.length / (left.length + right.length)) : start;
        mid = Math.round(mid * 100) / 100;
        var b = {};
        for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) b[k] = s[k];
        b.text = right; b.timestamp = mid;
        s.text = left;
        if (hasEnd) s.end = mid;
        segs.splice(index + 1, 0, b);
        return true;
    }

    /**
     * Отдать говорящему ЧАСТЬ текста: от места off0 в сегменте i0 до места off1 в
     * сегменте i1. Края при надобности разрезаются.
     * @returns {number} сколько сегментов получили новую метку
     */
    function assignText(segs, i0, off0, i1, off1, name) {
        name = clean(name);
        if (!name || i0 < 0 || i1 < i0 || !segs[i0] || !segs[i1]) return 0;
        /* Сначала правый край: разрез слева сдвинул бы его номер. */
        var lastLen = String(segs[i1].text || '').length;
        if (off1 > 0 && off1 < lastLen) splitAt(segs, i1, off1);
        var first = i0;
        if (off0 > 0 && splitAt(segs, i0, off0)) { first = i0 + 1; if (i1 >= i0) i1 += 1; }
        return assign(segs, first, i1, name);
    }

    /** Снимок расшифровки — для «Отменить»: и метки, и разрезы. */
    function snapshot(segs) {
        var out = new Array(segs.length);
        for (var i = 0; i < segs.length; i++) {
            var c = {}, s = segs[i] || {};
            for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) c[k] = s[k];
            out[i] = c;
        }
        return out;
    }

    /** Вернуть расшифровку из снимка — на месте, тем же массивом. */
    function restore(segs, snap) {
        if (!snap) return false;
        segs.length = 0;
        for (var i = 0; i < snap.length; i++) segs.push(snap[i]);
        return true;
    }

    root.__transcriptSpeakers = {
        names: names, has: has, clash: clash, clean: clean,
        rename: rename, merge: merge, assign: assign, assignText: assignText, splitAt: splitAt,
        span: span, freeName: freeName, snapshot: snapshot, restore: restore,
    };
})(typeof window !== 'undefined' ? window : globalThis);
